import type { Editor } from '@tiptap/core';
import { useEditorState } from '@tiptap/react';
import { useEffect, useState } from 'react';
import { Field } from './inspectorFields';

type TextStylingSectionProps = {
  editor: Editor | null;
  disabled?: boolean;
  onContentChange: () => void;
};

const DEFAULT_STYLING_STATE = {
  hasSelection: false,
  isCodeActive: false,
  linkHref: '',
};

function stylingStateSelector(ctx: { editor: Editor | null }) {
  if (!ctx.editor) {
    return DEFAULT_STYLING_STATE;
  }

  return {
    hasSelection: !ctx.editor.state.selection.empty,
    isCodeActive: ctx.editor.isActive('code'),
    linkHref: (ctx.editor.getAttributes('link')['href'] as string) || '',
  };
}

export function TextStylingSection({ editor, disabled, onContentChange }: TextStylingSectionProps) {
  const [linkUrl, setLinkUrl] = useState('https://');

  const stylingState = useEditorState({
    editor,
    selector: stylingStateSelector,
  }) ?? DEFAULT_STYLING_STATE;

  useEffect(() => {
    if (stylingState.linkHref) {
      setLinkUrl(stylingState.linkHref);
    }
  }, [stylingState.linkHref]);

  const handleApplyLink = () => {
    if (!editor || !linkUrl.trim()) {
      return;
    }

    const applied = editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: linkUrl.trim(), target: '_blank' })
      .run();

    if (applied) {
      onContentChange();
    }
  };

  const handleRemoveLink = () => {
    if (!editor) {
      return;
    }

    const removed = editor.chain().focus().unsetLink().run();
    if (removed) {
      onContentChange();
      setLinkUrl('https://');
    }
  };

  const handleToggleCode = () => {
    if (!editor) {
      return;
    }

    const toggled = editor.chain().focus().toggleCode().run();
    if (toggled) {
      onContentChange();
    }
  };

  return (
    <div className='ee-insp__section'>
      <div className='ee-insp__title'>Styling</div>
      {!stylingState.hasSelection && (
        <div className='ee-insp__sublabel' style={{ marginBottom: 12 }}>
          Select text in the block first
        </div>
      )}
      <Field label='Link URL'>
        <input
          className='input'
          placeholder='https://…'
          value={linkUrl}
          disabled={disabled}
          onChange={(e) => setLinkUrl(e.target.value)}
        />
      </Field>
      <div className='ee-fieldrow' style={{ marginTop: 8 }}>
        <button
          type='button'
          className='ee-insp__action'
          disabled={disabled || !editor || !linkUrl.trim()}
          onClick={handleApplyLink}
        >
          Apply link
        </button>
        <button
          type='button'
          className='ee-insp__action ee-insp__action--muted'
          disabled={disabled || !editor}
          onClick={handleRemoveLink}
        >
          Remove link
        </button>
      </div>
      <Field label='Code'>
        <button
          type='button'
          className={`ee-insp__action ee-insp__action--full ee-insp__toggle ${stylingState.isCodeActive ? 'is-on' : ''}`}
          disabled={disabled || !editor}
          onClick={handleToggleCode}
        >
          {'<>'}
        </button>
      </Field>
    </div>
  );
}
